import { Component, Event, EventEmitter, Method, Prop, Watch } from '@stencil/core';


/**
 * A data storage component.
 * Takes a JSON string and makes the data available through an object.
 * Subsequently provided JSON strings will add to the object.
 */


@Component({
  tag: 'hylo-data',
  styleUrl: 'hylo-data.scss'
})
export class HyloData {

  /** @internal */
  constructor() {}


  /**
   * Will be parsed into an object and accessible through the "object" property.
   *
   * Any change will be immediately parsed and update/overwrite the "object" property.
   *
   * Needs to represent either an Object or Array.
   */
  @Prop() json: string = '';

  /**
   * An object which gets created by parsing the "json" property.
   */
  @Prop({mutable: true}) object: any = undefined;

  /**
   * Disables the parsing of the "json" property and makes it possible to use the "object" property as data storage directly.
   */
  @Prop() disableParsing: boolean;

  /**
   * Resets the "object" every time new JSON data is parsed. It does not exchanges the object with a new one
   * but empties the object and refills it with the new data.
   *
   * By default the new data gets added to the existing data.
   */
  @Prop() resetObject: boolean;

  /**
   * Creates the "object" as an object "{}".
   *
   * By default the "object" is created as an array "[]" which makes it possible to push array values onto the object
   * when the JSON data is an array. If the "object" is an object, array values are set by their index and
   * would overwrite an already existing value with the same index.
   */
  @Prop() createAsObject: boolean;

  /**
   * By default json object properties replacing "object" properties if they already exist on the first level.
   * If "deep" is set, the parser compares the properties of the json object and the "object" to the last level
   * and appends/replaces the properties accordingly.
   */
  @Prop() deep: boolean;


  /**
   * Fires when loaded and the "object" got populated.
   * Emits the "object".
   * @event
   */
  @Event() hyloDataLoaded: EventEmitter<any>;

  /**
   * Fires when the "object" got updated/overwritten.
   * Emits the "object".
   * @event
   */
  @Event() hyloDataUpdated: EventEmitter<any>;


  /**
   * Returns whether the component is fully loaded and initialised.
   *
   * As alternative, you can check if "object" is defined.
   */
  @Method()
  async isLoaded() {

    return this.loaded;
  }

  /**
   * Returns the data object.
   * The returned promise resolves when the object got populated.
   */
  @Method()
  getObject() {

    return this.data_promise;
  }


  private loaded: boolean = false;
  private data_resolve: Function;
  private data_promise: Promise<any> = new Promise((resolve) => {this.data_resolve = resolve});


  /** @internal*/
  @Watch('json')
  jsonChanged(value: string) {

    if (this.disableParsing) return;

    if (this.object === undefined) {

      this.object = this.createAsObject ? {} : [];
      this.fillObject(JSON.parse(this.json));

      this.loaded = true;
      this.data_resolve(this.object);
      this.hyloDataLoaded.emit(this.object);

    } else {

      this.fillObject(JSON.parse(value));
      this.hyloDataUpdated.emit(this.object);
    }
  }


  /** @internal */
  componentWillLoad() {

    if (this.json && !this.disableParsing && this.object === undefined) {

      this.object = this.createAsObject ? {} : [];
      try {
        this.fillObject(JSON.parse(this.json));
      } catch(e) {
        console.error('Error parsing data JSON string:');
        console.log(this.json);
        console.log(e);
      }

      this.loaded = true;
      this.data_resolve(this.object);
      this.hyloDataLoaded.emit(this.object);
    }
  }


  private fillObject(values: any) {

    if (this.resetObject) {
      Object.entries(this.object).forEach(([key]) => {
        delete this.object[key];
      });
    }

    if (!values || typeof values !== 'object') return;

    if (this.deep) {

      this.compareObject(values, this.object);

    } else {

      Object.entries(values).forEach(([key, value]) => {

        let index: number | string = key;
        if (!Number.isInteger(index)) {
          index = Number.parseInt(index);
          if (Number.isNaN(index) || index+'' !== key) index = null;
        }

        if (index !== null && (index as number) >= 0 && Array.isArray(this.object)) this.object.push(value);
        else this.object[key] = value;
      });
    }
  }

  private compareObject(values: any, target: any) {

    Object.entries(values).forEach(([key, value]) => {

      let index: number | string = key;
      if (!Number.isInteger(index)) {
        index = Number.parseInt(index);
        if (Number.isNaN(index) || index+'' !== key) index = null;
      }

      if (index !== null && (index as number) >= 0 && Array.isArray(target)) target.push(value);
      else {
        if (target.hasOwnProperty(key)) {
          if (typeof values[key] !== 'object' || typeof target[key] !== 'object') target[key] = values;
          else this.compareObject(values[key], target[key]);
        } else target[key] = value;
      }
    });
  }
}
